/**
 * Dashboard color palettes.
 *
 * A palette is one complete set of `--hb-*` values (see `src/styles.css`). The
 * user picks one palette for dark mode and one for light mode; `src/lib/theme.ts`
 * stores the two ids and mirrors them onto `<html data-palette-dark|light>`, and
 * `PALETTE_CSS` (rendered once by the dashboard shell) turns the ids into values.
 *
 * Third-party palettes use the hex values published by each project. Where a
 * project has no color for a role we need (a mid surface step, a readable muted
 * text), the value is derived and marked `derived` in a comment.
 */

import { DEFAULT_PALETTE_ID, type ThemeMode } from "./theme";

export { DEFAULT_PALETTE_ID };
export type PaletteMode = ThemeMode;

const TOKEN_NAMES = [
  "page",
  "sidebar",
  "surface",
  "inset",
  "raised",
  "border",
  "card",
  "border-hover",
  "text",
  "heading",
  "muted",
  "subtle",
  "faint",
  "placeholder",
  "ghost",
  "accent",
  "accent-hover",
  "accent-fg",
  "success",
  "danger",
  "danger-hover",
  "danger-text",
  "danger-border",
  "danger-surface",
  "warning",
  "info",
  "ring",
  "scrollbar",
  "scrollbar-hover",
] as const;

export type PaletteToken = (typeof TOKEN_NAMES)[number];

/** The roles a palette has to supply; everything else is derived from these. */
type PaletteRoles = {
  /** Content panel background. */
  page: string;
  /** App chrome around the panel (top bar, sidebar, rail) and recessed wells. */
  sidebar: string;
  /** Cards, popovers, row hover. */
  surface: string;
  /** Selected rows, active nav. */
  raised: string;
  /** Hairline dividers. */
  border: string;
  /** Card, input and popover borders. */
  card: string;
  borderHover: string;
  heading: string;
  text: string;
  /** Secondary reading text: must clear 4.5:1 on `page`. */
  muted: string;
  /** Labels and captions: must clear 4.5:1 on `page`. */
  faint: string;
  placeholder: string;
  accent: string;
  accentHover: string;
  /** Text and icons on an accent fill. */
  accentFg: string;
  success: string;
  danger: string;
  warning: string;
  /**
   * Informational status and the second chart series. Must not be the accent's
   * hue, or an info badge next to an accent one reads as the same thing.
   */
  info: string;
};

export type Palette = {
  id: string;
  name: string;
  mode: PaletteMode;
  /**
   * Classic light is not a palette of its own: it is the classic dark tokens
   * under the site-wide inversion filter, exactly as light mode worked before
   * palettes existed. Every other light palette turns that filter off.
   */
  inverted?: boolean;
  /** Orb gradient stops, highlight to shadow. */
  orb: [string, string, string];
  tokens: Record<PaletteToken, string>;
};

export type PaletteFamily = {
  id: string;
  name: string;
  description: string;
  dark?: Palette;
  light?: Palette;
};

function definePalette(
  id: string,
  name: string,
  mode: PaletteMode,
  roles: PaletteRoles,
  options: { inverted?: boolean; orb?: [string, string, string] } = {},
): Palette {
  const dark = mode === "dark";
  return {
    id,
    name,
    mode,
    inverted: options.inverted,
    orb:
      options.orb ??
      (dark
        ? [roles.accent, roles.raised, roles.sidebar]
        : [roles.page, roles.raised, roles.accent]),
    tokens: {
      page: roles.page,
      sidebar: roles.sidebar,
      surface: roles.surface,
      inset: roles.sidebar,
      raised: roles.raised,
      border: roles.border,
      card: roles.card,
      "border-hover": roles.borderHover,
      text: roles.text,
      heading: roles.heading,
      muted: roles.muted,
      subtle: roles.muted,
      faint: roles.faint,
      placeholder: roles.placeholder,
      ghost: roles.muted,
      accent: roles.accent,
      "accent-hover": roles.accentHover,
      "accent-fg": roles.accentFg,
      success: roles.success,
      danger: roles.danger,
      "danger-hover": `color-mix(in oklab, ${roles.danger} 82%, ${dark ? "#ffffff" : "#000000"})`,
      "danger-text": `color-mix(in oklab, ${roles.danger} ${dark ? 60 : 85}%, ${roles.heading})`,
      "danger-border": `color-mix(in oklab, ${roles.danger} 45%, ${roles.page})`,
      "danger-surface": `color-mix(in oklab, ${roles.danger} 12%, ${roles.page})`,
      warning: roles.warning,
      info: roles.info,
      ring: `color-mix(in oklab, ${roles.accent} 50%, transparent)`,
      scrollbar: dark ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.16)",
      "scrollbar-hover": dark ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.28)",
    },
  };
}

/* ── Bavimail ───────────────────────────────────────────────── */

// The original near-black blueprint surfaces. The text ramp is lifted a little
// from the first release (muted was #8a8a8a) so small labels stay readable.
const CLASSIC_ROLES: PaletteRoles = {
  page: "#0d0d0f",
  sidebar: "#0a0a0c",
  surface: "#111114",
  raised: "#1a1a1e",
  border: "#1a1a1e",
  card: "#2a2a2e",
  borderHover: "#444444",
  heading: "#ffffff",
  text: "#d0d0d0",
  muted: "#a0a0a0",
  faint: "#8c8c8c",
  placeholder: "#757575",
  accent: "#f6821f",
  accentHover: "#ff8c2e",
  accentFg: "#000000",
  success: "#10b981",
  danger: "#ef4444",
  warning: "#f59e0b",
  info: "#38bdf8",
};

const classicDark = definePalette("classic", "Classic", "dark", CLASSIC_ROLES);
const classicLight = definePalette("classic-light", "Classic Light", "light", CLASSIC_ROLES, {
  inverted: true,
  orb: ["#ffffff", "#e6e6ea", "#f6821f"],
});

// Keep in sync with the :root defaults in src/styles.css.
const charcoal = definePalette("charcoal", "Charcoal", "dark", {
  page: "#1b1a20",
  sidebar: "#141318",
  surface: "#222128",
  raised: "#2b2a32",
  border: "#2a2930",
  card: "#38363f",
  borderHover: "#55525e",
  heading: "#f3f2f6",
  text: "#d6d4dc",
  muted: "#a5a2ad",
  faint: "#918e9a",
  placeholder: "#7d7a86",
  accent: "#f6821f",
  accentHover: "#ff8c2e",
  accentFg: "#000000",
  success: "#10b981",
  danger: "#ef4444",
  warning: "#f59e0b",
  info: "#38bdf8",
});

// Charcoal's light counterpart: the same cool-violet gray family, on paper.
// The orange is deepened so it still reads as text and icons on a light field.
const paper = definePalette("paper", "Paper", "light", {
  page: "#fcfbfd",
  sidebar: "#eeedf2",
  surface: "#f5f4f8",
  raised: "#e4e2ea",
  border: "#e6e4ec",
  card: "#d5d2dd",
  borderHover: "#b4b0c0",
  heading: "#18171c",
  text: "#34323b",
  muted: "#5f5c6a",
  faint: "#6c6977",
  placeholder: "#8e8b99",
  accent: "#c2570a",
  accentHover: "#a94a07",
  accentFg: "#ffffff",
  success: "#0f7a55",
  danger: "#c81e1e",
  warning: "#a15c07",
  info: "#0369a1",
});

/* ── GitHub (primer/primitives) ─────────────────────────────── */

const githubDark = definePalette("github-dark", "GitHub Dark", "dark", {
  page: "#0d1117", // canvas.default
  sidebar: "#010409", // canvas.inset
  surface: "#161b22", // canvas.subtle
  raised: "#21262d", // gray 7
  border: "#21262d", // border.muted
  card: "#30363d", // border.default
  borderHover: "#484f58", // gray 5
  heading: "#f0f6fc", // gray 0
  text: "#c9d1d9", // gray 1
  muted: "#8b949e", // gray 3
  faint: "#848d97", // fg.muted
  placeholder: "#6e7681", // fg.subtle
  accent: "#2f81f7", // accent.fg
  accentHover: "#58a6ff",
  accentFg: "#010409",
  success: "#3fb950",
  danger: "#f85149",
  warning: "#d29922",
  info: "#a371f7",
});

const githubLight = definePalette("github-light", "GitHub Light", "light", {
  page: "#ffffff", // canvas.default
  sidebar: "#f6f8fa", // canvas.inset
  surface: "#f6f8fa", // canvas.subtle
  raised: "#eaeef2", // gray 1
  border: "#d8dee4", // border.muted
  card: "#d0d7de", // border.default
  borderHover: "#afb8c1", // gray 3
  heading: "#1f2328", // fg.default
  text: "#32383f", // gray 8
  muted: "#57606a", // gray 6
  faint: "#656d76", // fg.muted
  placeholder: "#8c959f", // gray 4
  accent: "#0969da", // accent.fg
  accentHover: "#0550ae",
  accentFg: "#ffffff",
  success: "#1a7f37",
  danger: "#d1242f",
  warning: "#9a6700",
  info: "#8250df",
});

/* ── One Dark / One Light (atom/atom, One Dark Pro chrome) ──── */

const oneDark = definePalette("one-dark", "One Dark", "dark", {
  page: "#282c34",
  sidebar: "#21252b",
  surface: "#2c313a",
  raised: "#353b45", // derived: one step above the list hover
  border: "#1e2227", // derived from the #181a1f group border, softened
  card: "#3e4452",
  borderHover: "#4b5263",
  heading: "#d7dae0",
  text: "#abb2bf", // mono-1
  muted: "#9299a6", // derived: mono-2 #828997 lifted to clear 4.5:1
  faint: "#9097a4", // derived
  placeholder: "#6b7280", // derived from mono-3 #5c6370
  accent: "#61afef", // blue
  accentHover: "#7dbdf2",
  accentFg: "#1b1d23",
  success: "#98c379",
  danger: "#e06c75",
  warning: "#e5c07b",
  info: "#56b6c2",
});

const oneLight = definePalette("one-light", "One Light", "light", {
  page: "#fafafa",
  sidebar: "#eaeaeb",
  surface: "#f2f2f3", // derived: between bg and chrome
  raised: "#dbdbdc",
  border: "#e0e0e1",
  card: "#cfcfd1",
  borderHover: "#a0a1a7", // mono-3
  heading: "#232428",
  text: "#383a42", // mono-1
  muted: "#5f626c", // derived: mono-2 #696c77 deepened for chrome contrast
  faint: "#696c77", // mono-2
  placeholder: "#a0a1a7", // mono-3
  accent: "#4463e6", // derived: accent #526fff deepened to carry white text
  accentHover: "#3552cf",
  accentFg: "#ffffff",
  success: "#3c843b", // derived: deepened until the fill carries accent-fg at 4.5:1
  danger: "#ca1243", // red-2
  warning: "#986801",
  info: "#007cb0", // derived: deepened until the fill carries accent-fg at 4.5:1
});

/* ── Dracula / Alucard (dracula/dracula-theme) ──────────────── */

const dracula = definePalette("dracula", "Dracula", "dark", {
  page: "#282a36", // Background
  sidebar: "#21222c", // BGDark (VS Code port)
  surface: "#2f3241", // derived: between Background and BGLight
  raised: "#44475a", // Selection
  border: "#343746", // BGLight
  card: "#44475a",
  borderHover: "#6272a4", // Comment
  heading: "#ffffff",
  text: "#f8f8f2", // Foreground
  muted: "#a9b1d6", // derived: Comment #6272a4 is 3:1 on Background, too dim to read
  faint: "#979fc4", // derived
  placeholder: "#7f89b8", // derived
  accent: "#bd93f9", // Purple
  accentHover: "#caa9fa",
  accentFg: "#21222c",
  success: "#50fa7b",
  danger: "#ff5555",
  warning: "#ffb86c",
  info: "#8be9fd",
});

const alucard = definePalette("alucard", "Alucard", "light", {
  page: "#fffbeb", // Background
  sidebar: "#f3edd6", // derived
  surface: "#f9f4df", // derived
  raised: "#e8e1c8", // derived
  border: "#e9e2c9", // derived
  card: "#d8d0b3", // derived
  borderHover: "#b3aa88", // derived
  heading: "#1f1f1f", // Foreground
  text: "#2e2d28",
  muted: "#6c664b", // Comment
  faint: "#6c664b",
  placeholder: "#8d866a", // derived
  accent: "#644ac9", // Purple
  accentHover: "#5339b5",
  accentFg: "#ffffff",
  success: "#14710a",
  danger: "#cb3a2a",
  warning: "#846e15",
  info: "#036a96",
});

/* ── Catppuccin (catppuccin/palette) ────────────────────────── */

const mocha = definePalette("catppuccin-mocha", "Mocha", "dark", {
  page: "#1e1e2e", // base
  sidebar: "#181825", // mantle
  surface: "#27273a", // derived: between base and surface0
  raised: "#313244", // surface0
  border: "#313244", // surface0
  card: "#45475a", // surface1
  borderHover: "#585b70", // surface2
  heading: "#cdd6f4", // text
  text: "#bac2de", // subtext1
  muted: "#a6adc8", // subtext0
  faint: "#9399b2", // overlay2
  placeholder: "#7f849c", // overlay1
  accent: "#cba6f7", // mauve
  accentHover: "#d9bff9",
  accentFg: "#11111b", // crust
  success: "#a6e3a1",
  danger: "#f38ba8",
  warning: "#f9e2af",
  info: "#94e2d5",
});

const latte = definePalette("catppuccin-latte", "Latte", "light", {
  page: "#eff1f5", // base
  sidebar: "#e6e9ef", // mantle
  surface: "#e6e9ef", // mantle
  raised: "#ccd0da", // surface0
  border: "#dce0e8", // crust
  card: "#ccd0da", // surface0
  borderHover: "#acb0be", // surface2
  heading: "#4c4f69", // text
  text: "#4c4f69",
  muted: "#5c5f77", // subtext1
  faint: "#5c5f77",
  placeholder: "#8c8fa1", // overlay1
  accent: "#8839ef", // mauve
  accentHover: "#7528d8",
  accentFg: "#ffffff",
  success: "#348323", // derived: deepened until the fill carries accent-fg at 4.5:1
  danger: "#d20f39",
  warning: "#a06614", // derived: deepened until the fill carries accent-fg at 4.5:1
  info: "#148086", // derived: deepened until the fill carries accent-fg at 4.5:1
});

/* ── Gruvbox, medium contrast (morhetz/gruvbox) ─────────────── */

const gruvboxDark = definePalette("gruvbox-dark", "Gruvbox Dark", "dark", {
  page: "#282828", // bg0
  sidebar: "#1d2021", // bg0_h
  surface: "#32302f", // bg0_s
  raised: "#3c3836", // bg1
  border: "#3c3836", // bg1
  card: "#504945", // bg2
  borderHover: "#665c54", // bg3
  heading: "#fbf1c7", // fg0
  text: "#ebdbb2", // fg1
  muted: "#bdae93", // fg3
  faint: "#a89984", // fg4
  placeholder: "#928374", // gray
  accent: "#fe8019", // bright orange
  accentHover: "#fabd2f", // bright yellow
  accentFg: "#1d2021",
  success: "#b8bb26",
  danger: "#fb4934",
  warning: "#fabd2f",
  info: "#83a598",
});

const gruvboxLight = definePalette("gruvbox-light", "Gruvbox Light", "light", {
  page: "#fbf1c7", // bg0
  sidebar: "#f2e5bc", // bg0_s
  surface: "#f9f5d7", // bg0_h
  raised: "#ebdbb2", // bg1
  border: "#ebdbb2", // bg1
  card: "#d5c4a1", // bg2
  borderHover: "#bdae93", // bg3
  heading: "#282828", // fg0
  text: "#3c3836", // fg1
  muted: "#504945", // fg2
  faint: "#665c54", // fg3
  placeholder: "#928374", // gray
  accent: "#af3a03", // faded orange
  accentHover: "#8f2f02",
  accentFg: "#fbf1c7",
  success: "#746f0d", // derived: deepened until the fill carries accent-fg at 4.5:1
  danger: "#9d0006",
  warning: "#946010", // derived: deepened until the fill carries accent-fg at 4.5:1
  info: "#076678",
});

export const PALETTE_FAMILIES: PaletteFamily[] = [
  {
    id: "charcoal",
    name: "Charcoal",
    description: "The default. Soft charcoal, or paper in light mode.",
    dark: charcoal,
    light: paper,
  },
  {
    id: "classic",
    name: "Bavimail Classic",
    description: "The original near-black blueprint and its inverted light mode.",
    dark: classicDark,
    light: classicLight,
  },
  {
    id: "github",
    name: "GitHub",
    description: "Primer's dark and light.",
    dark: githubDark,
    light: githubLight,
  },
  {
    id: "one",
    name: "One Dark",
    description: "Atom's One Dark and One Light.",
    dark: oneDark,
    light: oneLight,
  },
  {
    id: "dracula",
    name: "Dracula",
    description: "Dracula, with Alucard for light mode.",
    dark: dracula,
    light: alucard,
  },
  {
    id: "catppuccin",
    name: "Catppuccin",
    description: "Mocha and Latte.",
    dark: mocha,
    light: latte,
  },
  {
    id: "gruvbox",
    name: "Gruvbox",
    description: "Retro groove, medium contrast.",
    dark: gruvboxDark,
    light: gruvboxLight,
  },
];

export const PALETTES: Palette[] = PALETTE_FAMILIES.flatMap((family) =>
  [family.dark, family.light].filter((palette): palette is Palette => Boolean(palette)),
);

export function findPalette(id: string | null | undefined, mode: PaletteMode): Palette {
  return (
    PALETTES.find((palette) => palette.id === id && palette.mode === mode) ??
    PALETTES.find((palette) => palette.id === DEFAULT_PALETTE_ID[mode])!
  );
}

function paletteRule(palette: Palette) {
  const selector = `html[data-theme="${palette.mode}"][data-palette-${palette.mode}="${palette.id}"]`;
  const declarations = TOKEN_NAMES.map((name) => `--hb-${name}:${palette.tokens[name]}`).join(";");
  let css = `${selector}{${declarations}}`;
  if (palette.mode === "light" && !palette.inverted) {
    // A real light palette replaces the inversion filter (see styles.css). The
    // extra attribute outranks the base rules without needing !important.
    css += `${selector}{filter:none}`;
    css += `${selector} :is(img,video,canvas,[data-theme-preserve]){filter:none}`;
  }
  return css;
}

/** Rendered by the dashboard and token-based content layouts; the legacy landing page keeps its filter theme. */
export const PALETTE_CSS = PALETTES.map(paletteRule).join("\n");

/** The palette in the shape `createClerkAppearance` takes (src/lib/clerk-appearance.ts). */
export function paletteClerkColors(palette: Palette) {
  const t = palette.tokens;
  return {
    page: t.page,
    inset: t.inset,
    surface: t.surface,
    raised: t.raised,
    border: t.border,
    card: t.card,
    borderHover: t["border-hover"],
    text: t.text,
    heading: t.heading,
    muted: t.muted,
    faint: t.faint,
    placeholder: t.placeholder,
    accent: t.accent,
    accentHover: t["accent-hover"],
    accentFg: t["accent-fg"],
    success: t.success,
    danger: t.danger,
    warning: t.warning,
    ring: t.ring,
  };
}
